import React, { useState, useEffect, useCallback } from 'react';
import AdminSidebar from '../../Sidebar/AdminSidebar';
import AdminNavbar from '../../Navbars/AdminNavbar';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { SuperAdminApis } from '../../../apis/superAdminApi';
import { Oval } from 'react-loader-spinner';
import {
    AlertTriangle, RefreshCw, Play, Eye, X, Info, CheckCircle2, Download,
} from 'lucide-react';

/** The message the API actually sent, rather than a generic failure. */
const apiError = (e: any, fallback: string): string => {
    const data = e?.response?.data;
    const fieldErrors = data?.errors ? Object.values(data.errors).flat().filter(Boolean) : [];
    if (fieldErrors.length) return fieldErrors.join(' ');
    return data?.message || e?.message || fallback;
};

const formatBytes = (n: number) => {
    if (!n) return '—';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

const formatDateTime = (v: string | null) => {
    if (!v) return '—';
    return new Date(v).toLocaleString('en-NG', {
        day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
};

const statusChip = (run: any) => {
    if (['queued', 'running', 'pending'].includes(run.parse_status)) return 'bg-amber-100 text-amber-700 border-amber-200';
    if (run.parse_status === 'failed') return 'bg-red-100 text-red-700 border-red-200';
    if (run.parse_status === 'empty') return 'bg-gray-100 text-gray-600 border-gray-200';
    return 'bg-green-100 text-green-700 border-green-200';
};

const statusLabel = (run: any) => {
    if (run.parse_status === 'queued') return 'Queued';
    if (run.parse_status === 'running') return 'Downloading…';
    if (run.parse_status === 'failed') return 'Failed';
    if (run.parse_status === 'empty') return 'Nothing waiting';
    if (run.parse_status === 'pending') return 'In progress';
    return 'Received';
};

function FfdxFeedLog() {
    const [runs, setRuns] = useState<any[]>([]);
    const [stats, setStats] = useState<any>(null);
    const [loader, setLoader] = useState<boolean>(true);
    const [running, setRunning] = useState<boolean>(false);
    const [mode, setMode] = useState<string>('');
    const [inFlight, setInFlight] = useState<any>(null);
    const [detail, setDetail] = useState<any>(null);
    const [detailLoader, setDetailLoader] = useState<boolean>(false);
    const [busy, setBusy] = useState<boolean>(false);
    const [staged, setStaged] = useState<any>(null);
    const [stagedState, setStagedState] = useState<string>('');
    const [stagedLoading, setStagedLoading] = useState<boolean>(false);

    const load = useCallback(async () => {
        setLoader(true);
        try {
            const [list, s] = await Promise.all([
                SuperAdminApis.getFfdxFeedRuns({ mode, per_page: 20 }),
                SuperAdminApis.getFfdxFeedStats(),
            ]);
            const rows = list?.success ? (list.data?.data || []) : [];
            setRuns(rows);
            if (s?.success) setStats(s.data);

            // A run in flight keeps the page polling until it settles.
            setInFlight(rows.find((r: any) => ['queued', 'running', 'pending'].includes(r.parse_status)) || null);
        } catch (e) {
            toast.error(apiError(e, 'Could not load the feed log'));
        } finally {
            setLoader(false);
        }
    }, [mode]);

    useEffect(() => { load(); }, [load]);

    // The download takes about a minute in a detached process, so the page watches
    // the run row rather than holding a request open waiting for it.
    useEffect(() => {
        if (!inFlight) return;
        const t = setInterval(load, 5000);
        return () => clearInterval(t);
    }, [inFlight, load]);

    /** A live run acknowledges, and that cannot be undone — so it is confirmed. */
    const runNow = async (chosen: 'dry' | 'live') => {
        if (chosen === 'live' && !window.confirm(
            'Run LIVE?\n\nThis acknowledges the batch with FFDX, which cannot be undone — '
            + 'they will not send it again. Use a dry run if you only want to look.'
        )) return;

        setRunning(true);
        try {
            const res = await SuperAdminApis.runFfdxFeed({ mode: chosen });
            if (res?.success) {
                toast.info(res.message);
            } else {
                toast.warn(res?.message || 'Could not start the download');
            }
            load();
        } catch (e) {
            toast.error(apiError(e, 'Could not reach FFDX'));
        } finally {
            setRunning(false);
        }
    };

    /** The stored XML is megabytes, so it is fetched as a file rather than rendered. */
    const downloadPayload = async (run: any) => {
        setBusy(true);
        try {
            const blob = await SuperAdminApis.downloadFfdxPayload(run.id);
            const url = window.URL.createObjectURL(new Blob([blob]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `ffdx-run-${run.id}.xml`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            toast.error(apiError(e, 'Could not download the payload'));
        } finally {
            setBusy(false);
        }
    };

    const loadStaged = useCallback(async (runId: any, state: string) => {
        setStagedLoading(true);
        try {
            const res = await SuperAdminApis.getFfdxRunShipments(runId, { state, per_page: 25 });
            if (res?.success) setStaged(res);
        } catch (e) {
            toast.error(apiError(e, 'Could not load the parsed shipments'));
        } finally {
            setStagedLoading(false);
        }
    }, []);

    /** Replays the parser over the stored payload — safe, and creates nothing on a dry run. */
    const reparse = async (run: any) => {
        setBusy(true);
        try {
            const res = await SuperAdminApis.reparseFfdxRun(run.id);
            if (res?.success) { toast.success(res.message); loadStaged(run.id, stagedState); load(); }
            else toast.warn(res?.message || 'Could not re-parse');
        } catch (e) {
            toast.error(apiError(e, 'Could not re-parse'));
        } finally {
            setBusy(false);
        }
    };

    const openDetail = async (run: any) => {
        setDetailLoader(true);
        setDetail(run);
        setStaged(null);
        try {
            const res = await SuperAdminApis.getFfdxFeedRun(run.id);
            if (res?.success) setDetail(res.data);
            setStagedState('');
            loadStaged(run.id, '');
        } catch (e) {
            toast.error(apiError(e, 'Could not load that run'));
        } finally {
            setDetailLoader(false);
        }
    };

    return (
        <>
            <AdminSidebar title="FFDX Feed" />
            <div className="relative md:ml-64 bg-[#FAFAFA] min-h-screen">
                <AdminNavbar title="FFDX Feed" />

                <div className="lg:px-10 px-6 pb-10 pt-4">

                    {/* Dry mode is the safe default and the page says so plainly. */}
                    <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-5 rounded-r">
                        <p className="font-semibold text-blue-900 flex items-center gap-2 text-sm">
                            <Info size={16} /> How this works
                        </p>
                        <p className="text-blue-800 text-sm mt-1">
                            A <strong>dry run</strong> asks FFDX what is waiting, stores it, and stops there — nothing is
                            created and FFDX keeps the batch, so you can run it as often as you like. A{' '}
                            <strong>live run</strong> acknowledges the batch, which tells FFDX not to send it again.
                        </p>
                    </div>

                    {/* If the server cannot start a background process, say so up front
                        rather than letting every run fail the same way. */}
                    {stats?.spawn && !stats.spawn.ok && (
                        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-5 rounded-r">
                            <p className="font-semibold text-red-900 flex items-center gap-2 text-sm">
                                <AlertTriangle size={16} /> The buttons below will not work on this server
                            </p>
                            <p className="text-red-800 text-sm mt-1">
                                {stats.spawn.reason} Until that is resolved, run the download from the console
                                instead: <code className="bg-red-100 px-1.5 py-0.5 rounded">php artisan ffdx:download</code>
                            </p>
                        </div>
                    )}

                    {inFlight && (
                        <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mb-5 rounded-r flex items-center gap-3">
                            <Oval height={18} width={18} color="#B45309" secondaryColor="#FDE68A" />
                            <p className="text-sm text-amber-900">
                                <strong>Run #{inFlight.id} is working.</strong> FFDX takes about a minute to answer —
                                this page updates on its own, you can leave it.
                            </p>
                        </div>
                    )}

                    {/* ── Controls ─────────────────────────────── */}
                    <div className="bg-white rounded-lg shadow p-5 mb-6">
                        <div className="flex flex-wrap items-end justify-between gap-3">
                            <div>
                                <h3 className="text-[17px] font-semibold text-[#1C1C1C]">Fetch from FFDX</h3>
                                <p className="text-xs text-gray-500 mt-1">
                                    Downloads manifests raised at other stations.
                                </p>
                            </div>
                            <div className="flex gap-2">
                                <button onClick={() => runNow('dry')} disabled={running || !!inFlight || (stats?.spawn && !stats.spawn.ok)}
                                    className="bg-gray-800 text-white px-4 py-2 rounded-md text-sm flex items-center gap-2 disabled:opacity-60">
                                    <Play size={15} /> {running ? 'Working…' : 'Dry run'}
                                </button>
                                <button onClick={() => runNow('live')} disabled={running || !!inFlight || (stats?.spawn && !stats.spawn.ok)}
                                    className="bg-[#E04343] text-white px-4 py-2 rounded-md text-sm flex items-center gap-2 disabled:opacity-60">
                                    <Download size={15} /> Live run
                                </button>
                            </div>
                        </div>

                        {stats && (
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-4 pt-4 border-t">
                                {[
                                    ['Runs', stats.total_runs],
                                    ['Dry', stats.dry_runs],
                                    ['Live', stats.live_runs],
                                    ['Failed', stats.failed],
                                    ['Shipments seen', stats.shipments_seen],
                                ].map(([label, value]: any, i: number) => (
                                    <div key={i} className={`rounded p-3 ${label === 'Failed' && value > 0 ? 'bg-red-50' : 'bg-gray-50'}`}>
                                        <p className="text-[11px] text-gray-500">{label}</p>
                                        <p className={`text-lg font-semibold ${label === 'Failed' && value > 0 ? 'text-[#E04343]' : 'text-gray-800'}`}>{value}</p>
                                    </div>
                                ))}
                            </div>
                        )}

                        {stats?.last_run_at && (
                            <p className="text-xs text-gray-500 mt-3">
                                Last run {formatDateTime(stats.last_run_at)}
                                {stats.last_ok_at && <> · last successful {formatDateTime(stats.last_ok_at)}</>}
                            </p>
                        )}
                    </div>

                    {/* ── Log ──────────────────────────────────── */}
                    <div className="flex flex-wrap justify-between items-center gap-3 mb-3">
                        <h3 className="text-[17px] font-semibold text-[#1C1C1C]">Run history</h3>
                        <div className="flex gap-2">
                            <select value={mode} onChange={(e) => setMode(e.target.value)}
                                className="px-3 py-2 border rounded-md text-sm bg-white">
                                <option value="">All runs</option>
                                <option value="dry">Dry only</option>
                                <option value="live">Live only</option>
                            </select>
                            <button onClick={load} className="bg-gray-200 text-gray-700 px-3 py-2 rounded-md text-sm">
                                <RefreshCw size={15} />
                            </button>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left text-gray-600 border-separate border-spacing-y-2">
                            <thead className="text-[12px] text-[#808080]">
                                <tr>
                                    <th className="px-3 py-2">When</th>
                                    <th className="px-3">Message ID</th>
                                    <th className="px-3">Mode</th>
                                    <th className="px-3">Manifests</th>
                                    <th className="px-3">Shipments</th>
                                    <th className="px-3">Payload</th>
                                    <th className="px-3">Outcome</th>
                                    <th className="px-3">Acknowledged</th>
                                    <th className="px-3">Status</th>
                                    <th className="px-3">By</th>
                                    <th className="px-3">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loader ? (
                                    <tr><td colSpan={11} className="text-center py-8">
                                        <div className="flex justify-center"><Oval height={34} width={34} color="#E04343" secondaryColor="#FCBF94" /></div>
                                    </td></tr>
                                ) : runs.length === 0 ? (
                                    <tr><td colSpan={11} className="text-center py-8 text-gray-400">
                                        No runs yet — try a dry run.
                                    </td></tr>
                                ) : runs.map((run: any) => (
                                    <tr key={run.id} className="bg-white">
                                        <td className="px-3 py-2.5 text-xs">{formatDateTime(run.created_at)}</td>
                                        <td className="px-3 font-mono text-xs">{run.message_id || '—'}</td>
                                        <td className="px-3">
                                            <span className={`px-2 py-0.5 rounded text-[11px] ${run.mode === 'live' ? 'bg-[#E04343]/10 text-[#E04343]' : 'bg-gray-100 text-gray-600'}`}>
                                                {run.mode}
                                            </span>
                                        </td>
                                        <td className="px-3">{run.manifest_count}</td>
                                        <td className="px-3 font-medium text-gray-800">{run.shipment_count}</td>
                                        <td className="px-3 text-xs text-gray-500">{formatBytes(run.response_bytes)}</td>
                                        <td className="px-3 text-xs">
                                            {run.mode === 'live'
                                                ? <>{run.staged_created} created{run.staged_skipped > 0 && <span className="text-gray-400">, {run.staged_skipped} existed</span>}</>
                                                : <>{run.staged_parsed} new{run.staged_matched > 0 && <span className="text-gray-400">, {run.staged_matched} held</span>}</>}
                                            {run.staged_failed > 0 && <span className="text-red-600"> · {run.staged_failed} failed</span>}
                                        </td>
                                        <td className="px-3 text-xs">
                                            {run.acknowledged_at
                                                ? <span className="text-green-700 flex items-center gap-1"><CheckCircle2 size={13} /> {formatDateTime(run.acknowledged_at)}</span>
                                                : run.mode === 'dry'
                                                    ? <span className="text-gray-400">not on a dry run</span>
                                                    : <span className="text-amber-700">no</span>}
                                        </td>
                                        <td className="px-3">
                                            <span className={`px-2 py-0.5 rounded-full border text-[11px] ${statusChip(run)}`}>
                                                {statusLabel(run)}
                                            </span>
                                        </td>
                                        <td className="px-3 text-xs text-gray-500">{run.triggered_by}</td>
                                        <td className="px-3">
                                            <button onClick={() => openDetail(run)} title="View" className="text-gray-500 hover:text-gray-800">
                                                <Eye size={16} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* ── Run detail: the raw evidence ─────────────── */}
            {detail && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[88vh] flex flex-col">
                        <div className="flex items-center justify-between px-5 py-3 border-b">
                            <div>
                                <h3 className="font-semibold">
                                    Run #{detail.id} · <span className="font-mono text-sm">{detail.message_id}</span>
                                </h3>
                                <p className="text-xs text-gray-500">
                                    {formatDateTime(detail.created_at)} · {detail.mode} run by {detail.triggered_by}
                                </p>
                            </div>
                            <button onClick={() => setDetail(null)} className="text-gray-400 hover:text-gray-700"><X size={18} /></button>
                        </div>

                        <div className="px-5 py-3 overflow-y-auto">
                            {detailLoader ? (
                                <div className="flex justify-center py-10"><Oval height={40} width={40} color="#E04343" secondaryColor="#FCBF94" /></div>
                            ) : (
                                <>
                                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                                        {[
                                            ['Manifests', detail.manifest_count],
                                            ['Shipments', detail.shipment_count],
                                            ['HTTP', detail.http_status || '—'],
                                            ['Ack code', detail.ack_code ?? '—'],
                                            ['Payload', formatBytes(detail.response_bytes)],
                                        ].map(([l, v]: any, i: number) => (
                                            <div key={i} className="bg-gray-50 rounded p-3">
                                                <p className="text-[11px] text-gray-500">{l}</p>
                                                <p className="font-semibold text-gray-800">{v}</p>
                                            </div>
                                        ))}
                                    </div>

                                    {detail.error && (
                                        <div className="bg-red-50 border-l-4 border-red-500 p-3 mb-4 rounded-r text-sm">
                                            <p className="font-semibold text-red-900 flex items-center gap-2">
                                                <AlertTriangle size={15} /> Error
                                            </p>
                                            <p className="text-red-800 mt-1 break-words">{detail.error}</p>
                                        </div>
                                    )}

                                    {/* What the parser made of it. On a dry run these are staged
                                        only — the comparison is the point, not the creation. */}
                                    {staged && (
                                        <div className="mb-5">
                                            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                                                <p className="text-xs font-semibold text-gray-600">
                                                    Parsed shipments ({staged.data?.total ?? 0})
                                                </p>
                                                <div className="flex gap-2">
                                                    <select value={stagedState}
                                                        onChange={(e) => { setStagedState(e.target.value); loadStaged(detail.id, e.target.value); }}
                                                        className="px-2 py-1 border rounded text-xs bg-white">
                                                        <option value="">All</option>
                                                        {Object.keys(staged.summary || {}).map((k) => (
                                                            <option key={k} value={k}>{k} ({staged.summary[k]})</option>
                                                        ))}
                                                    </select>
                                                    <button onClick={() => reparse(detail)} disabled={busy}
                                                        className="text-[11px] font-semibold bg-gray-200 text-gray-700 px-2.5 py-1 rounded flex items-center gap-1.5 disabled:opacity-60">
                                                        <RefreshCw size={12} /> Re-parse
                                                    </button>
                                                </div>
                                            </div>

                                            {Object.keys(staged.dockets || {}).length > 0 && (
                                                <div className="flex flex-wrap gap-2 mb-2">
                                                    {Object.entries(staged.dockets).map(([d, n]: any) => (
                                                        <span key={d} className="text-[11px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                                                            docket <strong>{d}</strong> · {n}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}

                                            <div className="overflow-auto max-h-72 border rounded">
                                                <table className="w-full text-xs">
                                                    <thead className="text-[11px] text-[#808080] bg-gray-50 border-b">
                                                        <tr>
                                                            <th className="text-left font-semibold px-2 py-2">Shipment</th>
                                                            <th className="text-left font-semibold px-2 py-2">Receiver</th>
                                                            <th className="text-left font-semibold px-2 py-2">State</th>
                                                            <th className="text-left font-semibold px-2 py-2">Weight</th>
                                                            <th className="text-left font-semibold px-2 py-2">Outcome</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {stagedLoading ? (
                                                            <tr><td colSpan={5} className="text-center py-6 text-gray-400">Loading…</td></tr>
                                                        ) : (staged.data?.data || []).length === 0 ? (
                                                            <tr><td colSpan={5} className="text-center py-6 text-gray-400">Nothing here.</td></tr>
                                                        ) : (staged.data?.data || []).map((r: any) => (
                                                            <tr key={r.id} className="border-t align-top">
                                                                <td className="px-2 py-1.5 font-medium text-gray-800">{r.shipment_number}</td>
                                                                <td className="px-2">{r.receiver_name}</td>
                                                                <td className="px-2">{r.receiver_state || <span className="text-gray-400 italic">(blank)</span>}</td>
                                                                <td className="px-2 whitespace-nowrap">{Number(r.weight)} kg</td>
                                                                <td className="px-2">
                                                                    <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                                                                        r.state === 'created' ? 'bg-green-100 text-green-700'
                                                                        : r.state === 'failed' ? 'bg-red-100 text-red-700'
                                                                        : r.state === 'matched' || r.state === 'skipped' ? 'bg-blue-100 text-blue-700'
                                                                        : 'bg-gray-100 text-gray-600'}`}>
                                                                        {r.state === 'matched' ? 'already held'
                                                                            : r.state === 'skipped' ? 'already existed'
                                                                            : r.state === 'parsed' ? 'new' : r.state}
                                                                    </span>
                                                                    {/* Where the feed disagrees with our copy. */}
                                                                    {r.diff && Object.keys(r.diff).length > 0 && (
                                                                        <div className="mt-1 space-y-0.5">
                                                                            {Object.entries(r.diff).map(([f, d]: any) => (
                                                                                <div key={f} className="text-[10px] text-amber-700">
                                                                                    {f}: <span className="font-mono">{String(d.feed || '—')}</span>
                                                                                    {' ≠ '}
                                                                                    <span className="font-mono">{String(d.ours || '—')}</span>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                    {r.error && <div className="mt-1 text-[10px] text-red-700">{r.error}</div>}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}

                                    {/* The payload is stored on disk — a download is megabytes of XML,
                                        so only the head is shown and the full file is fetched separately. */}
                                    {detail.payload_available && (
                                        <div className="mb-4">
                                            <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                                                <p className="text-xs font-semibold text-gray-600">
                                                    Response received{' '}
                                                    <span className="font-normal text-gray-400">
                                                        ({formatBytes(detail.response_bytes)}
                                                        {detail.response_truncated ? ' — showing the first part' : ''})
                                                    </span>
                                                </p>
                                                <button onClick={() => downloadPayload(detail)} disabled={busy}
                                                    className="text-[11px] font-semibold bg-gray-800 text-white px-2.5 py-1 rounded flex items-center gap-1.5 disabled:opacity-60">
                                                    <Download size={12} /> {busy ? 'Preparing…' : 'Download full XML'}
                                                </button>
                                            </div>
                                            <pre className="bg-gray-900 text-gray-100 text-[11px] p-3 rounded overflow-auto max-h-64 whitespace-pre-wrap break-words">
                                                {detail.response_preview}
                                            </pre>
                                        </div>
                                    )}

                                    {detail.response_bytes > 0 && !detail.payload_available && (
                                        <div className="bg-gray-50 border-l-4 border-gray-300 p-3 mb-4 rounded-r text-sm text-gray-600">
                                            The response was {formatBytes(detail.response_bytes)}, but the stored file is no
                                            longer on disk — it may have been cleared. The counts above still stand.
                                        </div>
                                    )}

                                    {[['Request sent', detail.request_xml], ['Acknowledgement reply', detail.ack_response]]
                                        .filter(([, body]) => !!body)
                                        .map(([label, body]: any) => (
                                            <div key={label} className="mb-4">
                                                <p className="text-xs font-semibold text-gray-600 mb-1">{label}</p>
                                                <pre className="bg-gray-900 text-gray-100 text-[11px] p-3 rounded overflow-auto max-h-64 whitespace-pre-wrap break-words">
                                                    {body}
                                                </pre>
                                            </div>
                                        ))}
                                </>
                            )}
                        </div>

                        <div className="flex justify-end px-5 py-3 border-t">
                            <button onClick={() => setDetail(null)} className="bg-gray-100 text-gray-700 px-4 py-2 rounded text-sm">Close</button>
                        </div>
                    </div>
                </div>
            )}

            <ToastContainer position="top-right" autoClose={5000} newestOnTop closeOnClick pauseOnHover />
        </>
    );
}

export default FfdxFeedLog;
